import { useCallback, useEffect, useState } from "react";
import { Download, FileText, Trash2 } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import {
  deleteSupportingDocument,
  fetchSupportingDocuments,
  getSupportingDocumentDownloadUrl,
} from "@/lib/api/supporting-documents";
import type { SupportingDocumentRecord, SupportingDocType } from "@/types";

function docTypeLabel(docType: SupportingDocType): string {
  return docType === "reference-contract" ? "Reference contract" : "Background material";
}

interface SupportingDocumentsListProps {
  reviewId: string;
  canDelete?: boolean;
  onChanged?: () => void;
  className?: string;
}

export function SupportingDocumentsList({
  reviewId,
  canDelete = false,
  onChanged,
  className,
}: SupportingDocumentsListProps) {
  const [docs, setDocs] = useState<SupportingDocumentRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDocs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const rows = await fetchSupportingDocuments(reviewId);
      setDocs(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load supporting documents");
    } finally {
      setIsLoading(false);
    }
  }, [reviewId]);

  useEffect(() => {
    void loadDocs();
  }, [loadDocs]);

  const handleDownload = useCallback(async (doc: SupportingDocumentRecord) => {
    const downloadUrl = await getSupportingDocumentDownloadUrl(doc.file_path);
    if (!downloadUrl) return;
    const link = window.document.createElement("a");
    link.href = downloadUrl;
    link.download = doc.file_name;
    link.target = "_blank";
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  }, []);

  const handleDelete = useCallback(
    async (docId: string) => {
      try {
        await deleteSupportingDocument(docId);
        setDocs((prev) => prev.filter((doc) => doc.id !== docId));
        onChanged?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to delete document");
      }
    },
    [onChanged]
  );

  if (isLoading) {
    return <p className="text-xs text-slate-500">Loading supporting documents…</p>;
  }

  if (error) {
    return <p className="text-xs text-red-400">{error}</p>;
  }

  if (!docs.length) {
    return null;
  }

  const referenceDocs = docs.filter((doc) => doc.doc_type === "reference-contract");
  const backgroundDocs = docs.filter((doc) => doc.doc_type === "background");

  function renderGroup(title: string, group: SupportingDocumentRecord[]) {
    if (!group.length) return null;
    return (
      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</p>
        <ul className="space-y-2">
          {group.map((doc) => (
            <li
              key={doc.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-[var(--review-config-border)] bg-[var(--surface-sunken)] px-3 py-2 dark:border-slate-700/50 dark:bg-slate-900/40"
            >
              <div className="flex min-w-0 items-center gap-2">
                <FileText className="h-4 w-4 shrink-0 text-violet-400" />
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-200">{doc.file_name}</p>
                  <p className="text-xs text-slate-500">{docTypeLabel(doc.doc_type)}</p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <IconButton size="sm" title="Download" onClick={() => void handleDownload(doc)}>
                  <Download className="h-4 w-4" />
                </IconButton>
                {canDelete ? (
                  <IconButton
                    size="sm"
                    variant="destructive"
                    title="Delete document"
                    onClick={() => void handleDelete(doc.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </IconButton>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className={className}>
      <p className="mb-2 text-xs text-slate-500">Supporting Documents</p>
      <div className="space-y-4">
        {renderGroup("Reference contracts", referenceDocs)}
        {renderGroup("Background material", backgroundDocs)}
      </div>
    </div>
  );
}
